import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CustomDialog, StyledDataGrid } from "../../../components/StyledComponents";
import {
    DialogActions,
    DialogContent,
    DialogTitle,
    Checkbox,
    MenuItem,
    Select,
} from "@mui/material";
import {
    ControlpanelPrimaryButton,
    ControlpanelSecondaryButton,
    GrayIconButton,
} from "../../../components/StyledButton";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { useLoading } from "../../../context/LoadingContext";
import { toast } from "react-toastify";
import DefaultTooltip from "../../../components/DefaultTooltip";
import QuestionIcon16pxSvg from "../../../assets/images/icons/QuestionIcon16pxSvg";
import ArrowRepeatIcon16pxSvg from "../../../assets/images/icons/ArrowRepeatIcon16pxSvg";
import CustomPagination from "../CustomPagination";
import NoRowOverlay from "../../../assets/images/icons/NoRowOverlay";
import Cookies from "js-cookie";
import { getUserList } from "../../../axios/User/UserApi";
import { updateAdminToGroup, updateUserToGroup } from "../../../axios/Group/GroupUrlApi";
import { useAuth } from "../../../context/AuthContext";
import { generateTranslateMuiTable } from "../../../utils/Function/commonFunction";
import { UpdateDisabled } from "@mui/icons-material";
import UserData from "../../../assets/data/UserData";
import CustomColumnsPanel from "../../../components/CustomColumnsPanel";
import CustomGridToolbarQuickFilterNew2 from "../CustomGridToolbarQuickFilterNew2";

const SelectGroupAdminDialog = ({
    isOpen,
    onClose,
    info,
    userGroup = [],
    adminGroup = [],
    handleRefreshInfo,
}) => {
    const { t } = useTranslation();
    const { profile } = useAuth();
    const [show, hide] = useLoading();
    const tableTranslated = useMemo(() => generateTranslateMuiTable(t), [t]);

    const [gridLoading, setGridLoading] = useState(false);

    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(50);

    // ✅ lưu các userId đang chọn admin (global)
    const [selectedUserIds, setSelectedUserIds] = useState([]);

    const [rowData, setRowData] = useState(
        (userGroup || []).map((user, index) => ({
            id: index + 1,
            userId: user.id ?? index + 1,
            isAdmin: selectedUserIds.includes(user.id ?? index + 1), // thêm isAdmin
            isManager: user.isManager || false,
            writePermission: user.writePermission ? String(user.writePermission) : "1",
            ...user,
        }))
    );

    useEffect(() => {
        if (!isOpen) return;
        if (!Array.isArray(userGroup)) return;

        // map adminGroup theo userId
        const adminMap = new Map(
            (adminGroup || []).map(a => [
                Number(a.userId),
                {
                    permission: String(a.permission),
                    isManager: a.isManager
                }
            ])
        );

        const rows = userGroup.map((u, index) => {
            const userId = Number(u.id);
            const adminInfo = adminMap.get(userId);

            return {
                id: index + 1,
                userId,
                ...u,

                // ✅ admin
                isAdmin: Boolean(adminInfo),

                // ✅ permission
                writePermission: adminInfo?.permission ?? "1",

                // ✅ manager (chỉ 1)
                isManager: adminInfo?.isManager ?? false
            };
        });

        setRowData(rows);

        // ✅ selectedUserIds = admin
        setSelectedUserIds(
            rows.filter(r => r.isAdmin).map(r => r.userId)
        );

    }, [isOpen, userGroup, adminGroup]);

    // ✅ init selectedUserIds từ userGroup khi mở dialog
    useEffect(() => {
        if (!isOpen) return;
        if (!Array.isArray(userGroup)) return;

        const initialSelected = userGroup.map((u) => Number(u.id));
        setSelectedUserIds(initialSelected);
    }, [isOpen, userGroup]);

    const rowCount = rowData.length;

    // ✅ checkbox 1 dòng
    const handleCheckboxChange = (userId, field, isChecked) => {
        setRowData(prev =>
            prev.map(r => {
                if (field === 'isAdmin') {
                    if (r.userId === userId) {
                        r.isAdmin = isChecked;
                        if (!isChecked) r.isManager = false;
                    }
                }

                if (field === 'isManager') {
                    if (r.userId === userId) {
                        r.isManager = isChecked;
                    } else {
                        // ❗ chỉ cho phép 1 manager
                        r.isManager = false;
                    }
                }

                return { ...r };
            })
        );

        if (field === 'isAdmin') {
            setSelectedUserIds(prev => {
                if (isChecked) return Array.from(new Set([...prev, userId]));
                return prev.filter(id => id !== userId);
            });
        }
    };

    // ✅ select all theo trang hiện tại (rowData)
    const handleSelectAllCheckbox = (isChecked) => {
        const pageUserIds = rowData.map(r => Number(r.userId));

        setRowData(prev =>
            prev.map(r => ({
                ...r,
                isAdmin: isChecked ? true : false,
                isManager: isChecked ? r.isManager : false,
            }))
        );

        setSelectedUserIds(prev => {
            if (isChecked) return Array.from(new Set([...prev, ...pageUserIds]));
            return prev.filter(id => !pageUserIds.includes(id));
        });
    };


    const allChecked = useMemo(() => {
        return rowData.length > 0 && rowData.every((r) => r.isAdmin);
    }, [rowData]);

    const someChecked = useMemo(() => {
        return rowData.some((r) => r.isAdmin);
    }, [rowData]);

    const handleWritePermissionChange = (event, row) => {
        const selectedValue = event.target.value;
        setRowData(prev =>
            prev.map(r => r.id === row.id ? { ...r, writePermission: selectedValue } : r)
        );
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        show();

        try {
            const admins = rowData.filter(r => r.isAdmin);

            // ✅ lấy đúng 1 manager
            const manager = admins.find(r => r.isManager === true);

            const payload = {
                group: info?.id,
                langId: localStorage.getItem("i18nextLngId"),
                userIds: admins.map(r => r.userId).join(","),          // string OK
                permissions: admins.map(r => r.writePermission).join(","), // string OK
                managerId: manager ? Number(manager.userId) : null     // ✅ INT
            };

            await updateAdminToGroup(payload);
            toast.success(t('common.update-user-to-group-successfully'));
            handleRefreshInfo();
            handleCloseDialog();
        } catch (error) {
            if (error.response && error.response.status !== 401) {
                toast.error(t(error.response.data));
            }
        } finally {
            hide();
            handleCloseDialog();
        }
    };

    // ✅ columns
    const columnData = useMemo(
        () => [
            {
                field: "isAdmin",
                headerName: t("common.content-077"),
                minWidth: 60,
                flex: 0.4,
                sortable: false,
                filterable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Checkbox
                        checked={allChecked}
                        indeterminate={!allChecked && someChecked}
                        onChange={(event) => handleSelectAllCheckbox(event.target.checked)}
                    />
                ),
                renderCell: (params) => (
                    <Checkbox
                        checked={params.row.isAdmin}
                        onChange={(event) =>
                            handleCheckboxChange(params.row.userId, 'isAdmin', event.target.checked)
                        }
                    />
                )

            },
            {
                field: "employeeCode",
                headerName: t("common.employee-code"),
                minWidth: 120,
                flex: 1,
            },
            {
                field: "fullName",
                headerName: t("common.full-name"),
                minWidth: 240,
                flex: 1.5,
            },
            { field: "email", headerName: "Email", minWidth: 220, flex: 1.4 },
            {
                field: 'writePermission', headerName: t("common.writePermission"), minWidth: 150, flex: 1,
                renderCell: (params) => (
                    <Select
                        value={params.row.writePermission}
                        onChange={(event) => handleWritePermissionChange(event, params.row)}
                        fullWidth
                        variant='standard'
                    >
                        <MenuItem value="1">{t('common.all')}</MenuItem>
                        <MenuItem disabled value="2">{t('common.writeonly')}</MenuItem>
                        <MenuItem disabled value="3">{t('common.readonly')}</MenuItem>
                    </Select>

                ),
            },
            {
                field: 'isManager',
                headerName: t("common.manager"),
                minWidth: 150,
                flex: 1,
                renderCell: (params) => (
                    <Checkbox
                        checked={params.row.isManager}
                        disabled={!params.row.isAdmin}
                        onChange={(event) =>
                            handleCheckboxChange(params.row.userId, 'isManager', event.target.checked)
                        }
                    />
                ),
            }

        ],
        [t, allChecked, someChecked, rowData]
    );

    // ✅ column visibility (cookie)
    const emailUser = profile?.email;
    const [columnVisibilityModel, setColumnVisibilityModel] = useState({
        employeeCode: true,
        fullName: true,
        email: true,
    });

    useEffect(() => {
        const saved = Cookies.get(`SelectGroupAdminDialog_${emailUser}`);
        if (saved) setColumnVisibilityModel(JSON.parse(saved));
    }, [emailUser]);

    const handleColumnVisibilityChange = (newModel) => {
        setColumnVisibilityModel(newModel);
        Cookies.set(`SelectGroupAdminDialog_${emailUser}`, JSON.stringify(newModel), {
            expires: 90,
            path: "/",
        });
    };

    const handleCloseDialog = () => onClose?.();

    const handleQuickFilterChange = (searchText) => {
        const lowerSearch = searchText.toLowerCase();
        const filtered = rowData.filter((row) =>
            Object.values(row).some((val) =>
                String(val).toLowerCase().includes(lowerSearch)
            )
        );
        setRowData(filtered);
    };

    const templateToolbar = useCallback(
        () => (
            <div className="flex gap-2 justify-between mb-1">
                <CustomGridToolbarQuickFilterNew2
                    onSearchChange={handleQuickFilterChange}
                />
            </div>
        ),
        [t]
    );

    if (!isOpen) return null;

    return (
        <CustomDialog
            maxWidth="xl"
            open
            onClose={handleCloseDialog}
            keepMounted
            transitionDuration={window.__tourIsOpen ? 0 : undefined}
            data-tour="user-group-button-group-admin-dialog"
        >
            <DialogTitle>
                <div className="flex justify-between">
                    <div className="flex items-center gap-4">
                        <p className="text-xl font-semibold">
                            {t("common.group-admins")}
                        </p>

                        <DefaultTooltip
                            title={t("tool-tip.add-edit-user-group")}
                            readMore={false}
                            content={
                                <div className="flex flex-col gap-3">
                                    <p>{t("tool-tip.this-view-allows-to-add-a-new-user-group")}.</p>
                                </div>
                            }
                        >
                            <QuestionIcon16pxSvg className="fill-[#BDBDBD]" />
                        </DefaultTooltip>
                    </div>

                    <GrayIconButton onClick={handleCloseDialog}>
                        <CloseIcon />
                    </GrayIconButton>
                </div>
            </DialogTitle>

            <DialogContent style={{ paddingBottom: "unset" }}>
                <div className="h-auto mt-2">
                    <StyledDataGrid
                        sx={{ height: "600px", width: "100%" }}
                        rows={rowData}
                        rowCount={rowCount}
                        columns={columnData}
                        getRowId={(r) => r.userId}
                        loading={gridLoading}
                        disableRowSelectionOnClick
                        slots={{
                            toolbar: templateToolbar,
                            pagination: CustomPagination,
                            noRowsOverlay: NoRowOverlay,
                            columnsPanel: CustomColumnsPanel,
                        }}
                        slotProps={{
                            pagination: {
                                page,
                                count: rowCount,
                                rowsPerPage: pageSize,
                                onPageChange: setPage,
                                onRowsPerPageChange: setPageSize,
                            },
                        }}
                        localeText={tableTranslated}
                        columnVisibilityModel={columnVisibilityModel}
                        onColumnVisibilityModelChange={handleColumnVisibilityChange}
                    />
                </div>
            </DialogContent>

            <DialogActions>
                <div className="flex gap-2 px-4 py-4">
                    <ControlpanelSecondaryButton onClick={handleCloseDialog}>
                        <p>{t("button.close")}</p>
                    </ControlpanelSecondaryButton>

                    <ControlpanelPrimaryButton onClick={handleSubmit}>
                        <p>{t("button.confirm")}</p>
                    </ControlpanelPrimaryButton>
                </div>
            </DialogActions>
        </CustomDialog>
    );
};

export default SelectGroupAdminDialog;
